import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  archiveUrl,
  copyEntry,
  createFolder,
  deleteEntries,
  downloadUrl,
  fetchConfig,
  fetchRoots,
  listFolder,
  renameEntry,
  restoreEntries,
  requestThumbnailTicket,
  statPaths,
  type FsEntry,
  type LinkKind,
  type StatEntry,
  type ThumbnailTicket,
} from '../api'
import { basename, breadcrumbs, formatBytes, formatDate, joinPath, parentPath } from '../format'
import { carriesEntries, packEntryDrag, wantsCopy } from '../entry-drag'
import {
  carriesFiles,
  pickedFromDrop,
  pickedFromInput,
  type PickedContents,
  type PickedFile,
} from '../picked-files'
import { useUndo } from '../undo'
import { canOpenInBrowser } from '../preview'
import { useActivity } from '../activity'
import { useAuth } from '../auth'
import ConflictDialog, { type Conflict } from '../components/ConflictDialog'
import RowMenu, { type RowAction } from '../components/RowMenu'
import ShareLinkDialog from '../components/ShareLinkDialog'
import AssignFolderDialog from '../components/AssignFolderDialog'
import EntryGrid from '../components/EntryGrid'
import EntryIcon from '../components/EntryIcon'
import SearchResults from '../components/SearchResults'

const VIEW_KEY = 'arthur-storage:view'

type SortKey = 'name' | 'size' | 'mtime'

interface Sort {
  by: SortKey
  ascending: boolean
}

/**
 * 폴더는 어떻게 정렬하든 위에 모아둔다. 크기순으로 세웠을 때 크기가 없는 폴더들이
 * 파일 사이에 흩어지면 목록이 읽히지 않는다 — 탐색기와 Finder 도 그렇게 한다.
 */
function sortEntries(entries: FsEntry[], sort: Sort): FsEntry[] {
  const direction = sort.ascending ? 1 : -1

  return [...entries].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'dir' ? -1 : 1

    switch (sort.by) {
      case 'size':
        return (a.size - b.size) * direction
      case 'mtime':
        return (a.mtime - b.mtime) * direction
      case 'name':
        return a.name.localeCompare(b.name, 'ko') * direction
    }
  })
}

function SortHeader({
  label,
  by,
  sort,
  onSort,
  numeric = false,
}: {
  label: string
  by: SortKey
  sort: Sort
  onSort: (by: SortKey) => void
  numeric?: boolean
}) {
  const active = sort.by === by
  return (
    <th
      className={numeric ? 'num' : ''}
      // 화면 낭독기에도 지금 무엇으로 정렬돼 있는지 알린다.
      aria-sort={active ? (sort.ascending ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="sort-header" onClick={() => onSort(by)}>
        {label}
        <span className={`sort-mark ${active ? 'on' : ''}`} aria-hidden="true">
          {active && !sort.ascending ? '▾' : '▴'}
        </span>
      </button>
    </th>
  )
}

export default function Browse() {
  const params = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { enqueue, onCompleted } = useActivity()
  const { offer: offerUndo, clear: clearUndo } = useUndo()
  const { user } = useAuth()

  const rootId = decodeURIComponent(params.rootId ?? '')
  const currentPath = `/${params['*'] ?? ''}`.replace(/\/+$/, '') || '/'

  /**
   * 찾는 말은 주소에 둔다. 뒤로 가기가 검색에서 빠져나오는 몸짓이 되고, 찾은 화면을
   * 그대로 다시 열 수도 있다.
   */
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q') ?? ''
  const [typed, setTyped] = useState(query)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<Conflict[] | null>(null)
  const [sharing, setSharing] = useState<
    { path: string; filename: string; kind: LinkKind } | null
  >(null)
  const [assigning, setAssigning] = useState<{ path: string; folderName: string } | null>(null)
  const [sort, setSort] = useState<Sort>({ by: 'name', ascending: true })
  /**
   * 목록이냐 격자냐. 폴더마다가 아니라 사람마다 정해지는 것이라 기억해둔다 — 사진을
   * 타일로 보는 사람은 다음 폴더에서도 타일로 본다.
   */
  const [view, setView] = useState<'list' | 'grid'>(
    () => (localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list'),
  )
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  /** 지금 이름을 고쳐 쓰고 있는 줄. */
  const [editing, setEditing] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const folderInput = useRef<HTMLInputElement>(null)
  const dragged = useRef<string[]>([])
  // Escape 로 물러날 때 뒤따르는 blur 가 이름을 고쳐버리지 않게 한다.
  const editInput = useRef<HTMLInputElement>(null)

  useEffect(() => setTyped(query), [query])

  const queryKey = useMemo(() => ['list', rootId, currentPath], [rootId, currentPath])
  const listing = useQuery({ queryKey, queryFn: () => listFolder(rootId, currentPath) })
  const roots = useQuery({ queryKey: ['roots'], queryFn: fetchRoots })
  const root = roots.data?.find((candidate) => candidate.id === rootId)

  /**
   * 이 폴더의 그림들을 부를 이름표. 파일마다 받으면 첫 장이 뜨기 전에 왕복이 백 번이다.
   *
   * 10분을 사는 티켓을 8분마다 새로 받는다. 새 티켓은 화면의 모든 썸네일 주소를 바꿔
   * 다시 받게 만드므로, 자주 바꾸는 것이 곧 낭비다.
   */
  const thumbTicket = useQuery({
    queryKey: ['thumb-ticket', rootId, currentPath],
    queryFn: () => requestThumbnailTicket(rootId, currentPath),
    staleTime: 8 * 60 * 1000,
    refetchInterval: 8 * 60 * 1000,
  })

  // Deployment shape, not user data: fetch it once and keep it.
  const appConfig = useQuery({ queryKey: ['config'], queryFn: fetchConfig, staleTime: Infinity })
  const isolatedOrigin = Boolean(appConfig.data?.contentOrigin)

  // Clear stale selection whenever the folder changes.
  useEffect(() => setSelected(new Set()), [rootId, currentPath])
  // 되돌리기는 방금 그 자리에서만 뜻이 있다. 폴더를 옮겨 다니면 치운다.
  useEffect(() => setEditing(null), [rootId, currentPath])

  /**
   * 고쳐 쓸 자리를 잡아준다.
   *
   * `autoFocus` 와 `onFocus` 로는 안 된다 — 포커스 이벤트가 끝난 뒤 브라우저가 커서를
   * 맨 뒤로 옮겨서, 골라둔 범위가 지워진다. 붙은 다음에 잡아야 남는다.
   */
  useEffect(() => {
    const node = editInput.current
    if (node === null || editing === null) return
    node.focus()
    // 확장자를 뺀 부분만 고른다. 고칠 자리가 거의 늘 거기다.
    const dot = editing.lastIndexOf('.')
    node.setSelectionRange(0, dot > 0 ? dot : editing.length)
  }, [editing])

  // F2 는 탐색기와 Finder 가 이름 고치기에 쓰는 자리다. 그대로 베끼면 배울 것이 없다.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'F2') return
      // 어딘가에 글을 쓰고 있는 중이면 그쪽 것이다.
      if (event.target instanceof HTMLElement && event.target.closest('input, textarea')) return
      const [only, ...rest] = [...selected]
      if (only === undefined || rest.length > 0) return
      event.preventDefault()
      setEditing(only)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [selected])

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey })
    void queryClient.invalidateQueries({ queryKey: ['roots'] })
  }, [queryClient, queryKey])

  // An upload landing in this folder should show up without a manual refresh.
  useEffect(
    () =>
      onCompleted((completedRoot, directory) => {
        if (completedRoot === rootId && directory === currentPath) invalidate()
      }),
    [onCompleted, rootId, currentPath, invalidate],
  )

  const readOnly = listing.data?.permission === 'read'

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setError(null)
      try {
        await action()
        invalidate()
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
      }
    },
    [invalidate],
  )

  /** 묻지 않고 휴지통으로 보내고, 되돌리기를 남긴다. */
  const removeMutation = useMutation({
    mutationFn: (names: string[]) =>
      deleteEntries(rootId, names.map((name) => joinPath(currentPath, name))),
    onSuccess: (result, names) => {
      invalidate()
      setSelected(new Set())
      offerUndo({
        message: `${names.length}개를 휴지통으로 보냈습니다.`,
        undo: () => restoreEntries(rootId, result.trashed.map((item) => item.id)),
      })
    },
    onError: (err: Error) => setError(err.message),
  })

  /**
   * Checks every destination before queueing anything. Files that collide go to
   * the dialog; the rest start uploading straight away.
   */
  const startUpload = useCallback(
    async (picked: PickedContents) => {
      if (picked.files.length === 0 && picked.emptyFolders.length === 0) return
      setError(null)

      try {
        // 파일이 하나도 없는 폴더는 업로드가 만들어주지 않는다. 여기서 만들지 않으면
        // 끌어다 놓은 폴더가 말없이 사라진다.
        for (const folder of picked.emptyFolders) {
          await createFolder(rootId, joinPath(currentPath, folder))
        }
        if (picked.emptyFolders.length > 0) invalidate()
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
        return
      }

      let existing: StatEntry[] = []
      try {
        existing = await statPaths(
          rootId,
          picked.files.map((entry) => joinPath(currentPath, entry.path)),
        )
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
        return
      }

      const clashing: Conflict[] = []
      const clear: PickedFile[] = []
      picked.files.forEach((entry, index) => {
        const found = existing[index]
        if (found?.exists) clashing.push({ picked: entry, existing: found })
        else clear.push(entry)
      })

      if (clear.length > 0) {
        enqueue(
          clear.map((entry) => ({ ...entry, onConflict: 'fail' as const })),
          rootId,
          currentPath,
        )
      }
      if (clashing.length > 0) setConflicts(clashing)
    },
    [rootId, currentPath, enqueue, invalidate],
  )

  /**
   * What a click on the name does: the thing the file itself suggests. A folder
   * opens, a file the browser can draw is shown, and everything else is taken.
   * Whichever it is, the row still offers both explicitly.
   */
  function openEntry(entry: FsEntry) {
    const target = joinPath(currentPath, entry.name)
    if (entry.type === 'dir') {
      navigate(`/r/${encodeURIComponent(rootId)}${target}`)
      return
    }
    if (canOpenInBrowser(entry.name, isolatedOrigin)) void openInTab(target)
    else void download(target)
  }

  /** A hidden anchor keeps the SPA mounted while the browser takes the file. */
  function navigateToDownload(url: string) {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.rel = 'noopener'
    document.body.append(anchor)
    anchor.click()
    anchor.remove()
  }

  async function download(path: string) {
    setError(null)
    try {
      navigateToDownload(await downloadUrl(rootId, path))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  /**
   * The tab is opened before the ticket request, not after: by the time an
   * awaited call resolves the click no longer counts as a user gesture, and
   * every popup blocker swallows the window.
   */
  async function openInTab(path: string) {
    setError(null)
    const tab = window.open('about:blank', '_blank')
    if (!tab) {
      setError('팝업이 차단되어 새 탭을 열지 못했습니다. 이 사이트의 팝업을 허용해 주세요.')
      return
    }
    // Disown the new tab while it is still about:blank and same-origin, which
    // is the only moment this assignment is allowed. Without it the file could
    // navigate the tab it came from -- the hub sends COOP for the same reason,
    // but that header only counts in a secure context, so an http deployment
    // would be left with nothing.
    tab.opener = null
    try {
      tab.location.replace(await downloadUrl(rootId, path, { inline: true }))
    } catch (err) {
      tab.close()
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function downloadZip(paths: string[]) {
    setError(null)
    try {
      navigateToDownload(await archiveUrl(rootId, paths))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  /**
   * 고른 것들을 다른 폴더로 옮긴다. 같은 root 안이므로 agent 에서 rename 한 번이다.
   *
   * 하나가 실패해도 나머지는 계속 간다. 이름이 겹치는 한 개 때문에 멈추면, 어디까지 갔는지
   * 사람이 알 수 없는 상태로 끝난다. 실패한 것은 제자리에 그대로 남는다.
   */
  async function moveInto(names: string[], destination: string) {
    setError(null)
    clearUndo()

    const done: string[] = []
    const failed: string[] = []

    for (const name of names) {
      try {
        await renameEntry(rootId, joinPath(currentPath, name), joinPath(destination, name))
        done.push(name)
      } catch (err) {
        failed.push(`${name} (${err instanceof Error ? err.message : String(err)})`)
      }
    }

    invalidate()
    setSelected(new Set())
    if (done.length > 0) {
      offerUndo({
        message: `${done.length}개를 ${destination === '/' ? '루트' : basename(destination)}(으)로 옮겼습니다.`,
        // 되돌리는 것도 같은 이동이다. 옮긴 이름 그대로 원래 자리로.
        undo: async () => {
          for (const name of done) {
            await renameEntry(rootId, joinPath(destination, name), joinPath(currentPath, name))
          }
        },
      })
    }
    if (failed.length > 0) setError(`${failed.length}개를 옮기지 못했습니다 — ${failed.join(', ')}`)
  }

  /**
   * 복사는 이름이 겹쳐도 묻지 않는다. 둘 다 남기고(`사진 (2).jpg`) 무엇이 그렇게 됐는지
   * 알려주는 편이, 파일마다 다이얼로그를 하나씩 띄우는 것보다 잃는 것이 없다.
   */
  async function copyInto(names: string[], destination: string) {
    setError(null)
    clearUndo()

    const created: string[] = []
    const renamed: string[] = []
    const failed: string[] = []
    for (const name of names) {
      try {
        const result = await copyEntry(
          rootId,
          joinPath(currentPath, name),
          joinPath(destination, name),
          'rename',
        )
        created.push(result.path)
        if (result.renamed) renamed.push(basename(result.path))
      } catch (err) {
        failed.push(`${name} (${err instanceof Error ? err.message : String(err)})`)
      }
    }

    invalidate()
    setSelected(new Set())
    if (created.length > 0) {
      const where = destination === '/' ? '루트' : basename(destination)
      const note = renamed.length > 0 ? ` 이름이 겹친 ${renamed.length}개는 새 이름으로.` : ''
      offerUndo({
        message: `${created.length}개를 ${where}(으)로 복사했습니다.${note}`,
        // 되돌리기는 방금 생긴 것만 지운다 — 원래 있던 같은 이름의 파일이 아니라.
        undo: () => deleteEntries(rootId, created),
      })
    }
    if (failed.length > 0) setError(`${failed.length}개를 복사하지 못했습니다 — ${failed.join(', ')}`)
  }

  /** 지금 끌고 있는 것을 이 폴더가 받을 수 있는지. */
  function accepts(event: DragEvent<HTMLElement>, destination: string): boolean {
    if (readOnly) return false
    if (!carriesEntries(event.dataTransfer)) return false
    if (destination === currentPath) return false
    // 자기 자신 안으로는 들어갈 수 없다.
    return !dragged.current.some((name) => joinPath(currentPath, name) === destination)
  }

  /** 놓을 수 있는 자리마다 같은 것을 붙인다 — 폴더 행과 빵부스러기. */
  /** 고른 것을 끌면 고른 것 전체가, 아니면 그 하나가 움직인다. 목록과 격자가 같이 쓴다. */
  function startEntryDrag(entry: FsEntry, event: DragEvent<HTMLElement>) {
    const names = selected.has(entry.name) ? [...selected] : [entry.name]
    dragged.current = names
    // 사이드바처럼 다른 컴포넌트가 받는 자리에서는 ref 를 볼 수 없다.
    event.dataTransfer.effectAllowed = 'copyMove'
    packEntryDrag(event.dataTransfer, { rootId, path: currentPath, names })
  }

  function dropZone(destination: string) {
    return {
      onDragOver: (event: DragEvent<HTMLElement>) => {
        // 여기서 멈추지 않으면 바깥(.browser)의 핸들러가 이어서 표시를 지운다.
        event.stopPropagation()
        if (!accepts(event, destination)) return
        event.preventDefault()
        // 커서가 알려준다. 설명을 읽지 않아도 되는 유일한 방법이다.
        event.dataTransfer.dropEffect = wantsCopy(event) ? 'copy' : 'move'
        setDropTarget(destination)
      },
      onDrop: (event: DragEvent<HTMLElement>) => {
        event.stopPropagation()
        if (!accepts(event, destination)) return
        event.preventDefault()
        setDropTarget(null)
        const names = dragged.current
        if (wantsCopy(event)) void copyInto(names, destination)
        else void moveInto(names, destination)
      },
    }
  }

  function commitRename(from: string, to: string) {
    setEditing(null)
    const name = to.trim()
    if (name === '' || name === from) return
    void run(() => renameEntry(rootId, joinPath(currentPath, from), joinPath(currentPath, name)))
  }

  /**
   * 이름을 묻지 않고 만든다. 빈 이름을 골라 폴더를 먼저 만들고, 그 줄을 고쳐 쓰는
   * 상태로 띄운다 — 그대로 Enter 면 `새 폴더` 로 남는다. Finder 와 탐색기가 하는 것과
   * 같고, 만들기 전에 이름부터 대답하게 하는 것보다 손이 덜 간다.
   */
  function addFolder() {
    const taken = new Set(entries.map((entry) => entry.name))
    let name = '새 폴더'
    for (let counter = 2; taken.has(name); counter += 1) name = `새 폴더 (${counter})`

    void run(async () => {
      await createFolder(rootId, joinPath(currentPath, name))
      setEditing(name)
    })
  }

  /**
   * 행 하나가 할 수 있는 일. 이름을 누르는 것이 그 항목이 권하는 행동이고, 나머지는 전부
   * 여기로 접는다 — 행마다 버튼 다섯 개가 늘어서면 목록이 읽히지 않는다.
   */
  function actionsFor(entry: FsEntry): RowAction[] {
    const target = joinPath(currentPath, entry.name)
    const actions: RowAction[] = []

    if (entry.type === 'file' && canOpenInBrowser(entry.name, isolatedOrigin)) {
      actions.push({ label: '열기', onSelect: () => void openInTab(target) })
    }
    actions.push(
      entry.type === 'dir'
        ? { label: 'ZIP으로 받기', onSelect: () => void downloadZip([target]) }
        : { label: '받기', onSelect: () => void download(target) },
    )
    if (entry.type === 'file') {
      actions.push({
        label: '공유 링크',
        onSelect: () => setSharing({ path: target, filename: entry.name, kind: 'download' }),
      })
    }
    // 받는 링크는 쓸 수 있는 폴더에만 걸 수 있다. 읽기 전용에 걸면 만들 때가 아니라
    // 보낸 사람이 파일을 올리는 순간에야 막힌다.
    if (entry.type === 'dir' && !readOnly) {
      actions.push({
        label: '업로드 링크',
        onSelect: () => setSharing({ path: target, filename: entry.name, kind: 'upload' }),
      })
    }
    // 폴더를 내주는 것은 노드 차원의 일이라 노드 루트에서만 할 수 있다.
    if (entry.type === 'dir' && user?.role === 'admin' && root?.kind === 'node') {
      actions.push({
        label: '할당',
        onSelect: () => setAssigning({ path: target, folderName: entry.name }),
      })
    }
    if (!readOnly) {
      actions.push({ label: '이름변경', onSelect: () => setEditing(entry.name) })
      actions.push({ label: '삭제', onSelect: () => removeMutation.mutate([entry.name]) })
    }

    return actions
  }

  function toggle(name: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  async function onDrop(event: DragEvent<HTMLDivElement>) {
    if (!carriesFiles(event.dataTransfer)) return
    // 파일 드래그는 읽기 전용일 때도 막아야 한다. 브라우저에 넘기면 놓은 파일이 이 페이지를
    // 밀어내고 그 자리에 열린다 — 올라가던 업로드까지 같이 사라진다.
    event.preventDefault()
    setDragging(false)
    if (readOnly) return
    await startUpload(await pickedFromDrop(event.dataTransfer))
  }

  const entries = sortEntries(listing.data?.entries ?? [], sort)
  const crumbs = breadcrumbs(currentPath)

  /** 같은 열을 다시 누르면 뒤집고, 다른 열이면 그 열의 자연스러운 방향으로 시작한다. */
  function sortBy(by: SortKey) {
    setSort((current) =>
      current.by === by
        ? { by, ascending: !current.ascending }
        : // 이름은 ㄱ부터가, 크기와 날짜는 큰 것·최근 것부터가 찾던 것일 때가 많다.
          { by, ascending: by === 'name' },
    )
  }

  return (
    <div
      className={`browser ${dragging ? 'dragging' : ''}`}
      onDragOver={(event) => {
        // 놓을 수 있는 자리는 저마다 stopPropagation 하므로, 여기까지 올라왔다는 것은
        // 받아줄 수 없는 자리 위에 있다는 뜻이다.
        setDropTarget(null)
        if (!carriesFiles(event.dataTransfer)) return
        event.preventDefault()
        // 커서로 받을지 말지를 알린다. 읽기 전용 폴더 위에서는 금지 표시가 뜬다.
        event.dataTransfer.dropEffect = readOnly ? 'none' : 'copy'
        if (!readOnly) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => void onDrop(event)}
    >
      <header className="browser-header">
        <nav className="crumbs">
          <button
            type="button"
            className={`link crumb ${dropTarget === '/' ? 'drop-target' : ''}`}
            onClick={() => navigate(`/r/${encodeURIComponent(rootId)}/`)}
            {...dropZone('/')}
          >
            루트
          </button>
          {crumbs.map((crumb) => (
            <span key={crumb.path}>
              <span className="sep">/</span>
              <button
                type="button"
                className={`link crumb ${dropTarget === crumb.path ? 'drop-target' : ''}`}
                onClick={() => navigate(`/r/${encodeURIComponent(rootId)}${crumb.path}`)}
                {...dropZone(crumb.path)}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </nav>

        <div className="toolbar">
          {readOnly && <span className="badge">읽기 전용</span>}
          {currentPath !== '/' && (
            <button
              type="button"
              onClick={() =>
                navigate(`/r/${encodeURIComponent(rootId)}${parentPath(currentPath)}`)
              }
            >
              상위로
            </button>
          )}
          <button type="button" onClick={() => void listing.refetch()}>
            새로고침
          </button>

          <button
            type="button"
            onClick={() => {
              const next = view === 'list' ? 'grid' : 'list'
              setView(next)
              localStorage.setItem(VIEW_KEY, next)
            }}
          >
            {view === 'list' ? '격자로' : '목록으로'}
          </button>

          {/* 격자에는 열 이름이 없어서 정렬할 자리가 없다. 보기를 바꿨다고 할 수 있던
              일이 사라지면 안 되므로 같은 손잡이를 여기에 둔다. */}
          {view === 'grid' && (
            <label className="inline">
              정렬
              <select
                value={sort.by}
                onChange={(event) => sortBy(event.target.value as SortKey)}
                aria-label="정렬 기준"
              >
                <option value="name">이름</option>
                <option value="size">크기</option>
                <option value="mtime">수정일</option>
              </select>
              <button
                type="button"
                aria-label={sort.ascending ? '오름차순, 눌러서 뒤집기' : '내림차순, 눌러서 뒤집기'}
                onClick={() => sortBy(sort.by)}
              >
                {sort.ascending ? '▲' : '▼'}
              </button>
            </label>
          )}

          <form
            className="search"
            onSubmit={(event) => {
              event.preventDefault()
              const value = typed.trim()
              // 빈 말로 찾으라는 것은 검색을 그만두겠다는 뜻이다.
              setSearchParams(value === '' ? {} : { q: value })
            }}
          >
            <input
              type="search"
              value={typed}
              placeholder="이름으로 찾기"
              aria-label="이름으로 찾기"
              onChange={(event) => setTyped(event.target.value)}
            />
            {query !== '' && (
              <button type="button" className="link" onClick={() => setSearchParams({})}>
                그만
              </button>
            )}
          </form>

          {!readOnly && (
            <>
              <button type="button" onClick={() => fileInput.current?.click()}>
                파일 업로드
              </button>
              <button type="button" onClick={() => folderInput.current?.click()}>
                폴더 업로드
              </button>
              <button type="button" onClick={addFolder}>
                새 폴더
              </button>
            </>
          )}

          {selected.size > 0 && (
            <>
              <span className="muted small">{selected.size}개 선택됨</span>
              <button
                type="button"
                onClick={() =>
                  void downloadZip([...selected].map((name) => joinPath(currentPath, name)))
                }
              >
                ZIP으로 받기
              </button>
              {!readOnly && (
                <button
                  type="button"
                  className="danger"
                  onClick={() => removeMutation.mutate([...selected])}
                >
                  삭제
                </button>
              )}
            </>
          )}
        </div>
      </header>

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          if (event.target.files) void startUpload(pickedFromInput(event.target.files))
          event.target.value = ''
        }}
      />
      <input
        ref={folderInput}
        type="file"
        multiple
        hidden
        // Non-standard but universally supported; enables folder selection.
        {...{ webkitdirectory: '' }}
        onChange={(event) => {
          if (event.target.files) void startUpload(pickedFromInput(event.target.files))
          event.target.value = ''
        }}
      />

      {/* Only the top level is affected; the mounted folders below are real disk. */}
      {root?.ephemeralRoot && currentPath === '/' && (
        <p className="notice banner">
          이 노드는 하위 폴더만 디스크에 연결돼 있습니다. 여기(최상위)에 파일을 두면 컨테이너를
          교체할 때 사라지므로 쓰기가 막혀 있습니다. 아래 폴더 안에서 작업하세요.
        </p>
      )}

      {error && (
        <p className="error banner" onClick={() => setError(null)}>
          {error}
        </p>
      )}

      {listing.isError && (
        <p className="error banner">{(listing.error as Error).message}</p>
      )}

      {/* 찾는 중에는 목록 대신 찾은 것을 보여준다. 지금 보고 있는 폴더 아래에서만
          찾으므로, 빵부스러기가 곧 찾는 범위다. */}
      {query !== '' ? (
        <SearchResults rootId={rootId} path={currentPath} query={query} />
      ) : view === 'grid' ? (
        <>
          {listing.isPending && <p className="muted center">불러오는 중…</p>}
          {listing.isSuccess && entries.length === 0 && (
            <p className="muted center">
              {readOnly ? '빈 폴더입니다.' : '빈 폴더입니다. 파일을 끌어다 놓아 업로드하세요.'}
            </p>
          )}
          <EntryGrid
            entries={entries}
            ticket={thumbTicket.data ?? null}
            selected={selected}
            onToggle={toggle}
            onOpen={openEntry}
            actionsFor={actionsFor}
            draggable={!readOnly}
            onDragStart={startEntryDrag}
            onDragEnd={() => {
              dragged.current = []
              setDropTarget(null)
            }}
            dropTarget={dropTarget}
            pathOf={(entry) => joinPath(currentPath, entry.name)}
            dropZone={dropZone}
          />
        </>
      ) : (
        <table className="entries">
          <thead>
            <tr>
              <th className="check">
                <input
                  type="checkbox"
                  aria-label="전체 선택"
                  checked={entries.length > 0 && selected.size === entries.length}
                  onChange={(event) =>
                    setSelected(event.target.checked ? new Set(entries.map((e) => e.name)) : new Set())
                  }
                />
              </th>
              <SortHeader label="이름" by="name" sort={sort} onSort={sortBy} />
              <SortHeader label="크기" by="size" sort={sort} onSort={sortBy} numeric />
              <SortHeader label="수정일" by="mtime" sort={sort} onSort={sortBy} numeric />
              <th className="actions" />
            </tr>
          </thead>
          <tbody>
            {listing.isPending && (
              <tr>
                <td colSpan={5} className="muted center">
                  불러오는 중…
                </td>
              </tr>
            )}
            {listing.isSuccess && entries.length === 0 && (
              <tr>
                <td colSpan={5} className="muted center">
                  {readOnly ? '빈 폴더입니다.' : '빈 폴더입니다. 파일을 끌어다 놓아 업로드하세요.'}
                </td>
              </tr>
            )}
            {entries.map((entry) => (
              <tr
                key={entry.name}
                className={[
                  selected.has(entry.name) ? 'selected' : '',
                  dropTarget === joinPath(currentPath, entry.name) ? 'drop-target' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDoubleClick={() => openEntry(entry)}
                draggable={!readOnly && editing !== entry.name}
                onDragStart={(event) => startEntryDrag(entry, event)}
                onDragEnd={() => {
                  dragged.current = []
                  setDropTarget(null)
                }}
                {...(entry.type === 'dir' ? dropZone(joinPath(currentPath, entry.name)) : {})}
              >
                <td className="check">
                  <input
                    type="checkbox"
                    aria-label={`${entry.name} 선택`}
                    checked={selected.has(entry.name)}
                    onChange={() => toggle(entry.name)}
                  />
                </td>
                <td>
                  {editing === entry.name ? (
                    <span className="entry-name editing">
                      <EntryIcon entry={entry} ticket={thumbTicket.data ?? null} />
                      <input
                        type="text"
                        ref={editInput}
                        defaultValue={entry.name}
                        aria-label={`${entry.name} 새 이름`}
                        // 딴 데를 누르면 저장한다. Finder 와 탐색기가 그렇게 한다.
                        onBlur={(event) => commitRename(entry.name, event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            commitRename(entry.name, event.currentTarget.value)
                          } else if (event.key === 'Escape') {
                            // 원래 이름으로 되돌려 놓고 닫는다. 브라우저가 사라지는 요소에
                            // blur 를 내주든 말든, 그때 읽히는 값이 제 이름이라 저장할 것이
                            // 없다 -- "무시하라" 는 표시를 따로 들고 다니면 그것이 세션을
                            // 넘어 남는다.
                            event.currentTarget.value = entry.name
                            setEditing(null)
                          }
                        }}
                      />
                    </span>
                  ) : (
                    <button type="button" className="entry-name" onClick={() => openEntry(entry)}>
                      <EntryIcon entry={entry} ticket={thumbTicket.data ?? null} />
                      {entry.name}
                    </button>
                  )}
                </td>
                <td className="num">{entry.type === 'dir' ? '-' : formatBytes(entry.size)}</td>
                <td className="num">{formatDate(entry.mtime)}</td>
                <td className="actions">
                  <RowMenu label={entry.name} actions={actionsFor(entry)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {dragging && <div className="drop-overlay">여기에 놓아 업로드</div>}

      {assigning && root && (
        <AssignFolderDialog
          nodeId={root.id.replace(/^node:/, '')}
          path={assigning.path}
          folderName={assigning.folderName}
          onClose={() => setAssigning(null)}
        />
      )}

      {sharing && (
        <ShareLinkDialog
          rootId={rootId}
          path={sharing.path}
          filename={sharing.filename}
          kind={sharing.kind}
          onClose={() => setSharing(null)}
        />
      )}

      {conflicts && (
        <ConflictDialog
          conflicts={conflicts}
          onCancel={() => setConflicts(null)}
          onResolve={(resolutions) => {
            setConflicts(null)
            // 건너뛴 것은 빼고, 남은 것은 고른 정책 그대로 올린다.
            const wanted = resolutions.flatMap(({ picked, choice }) =>
              choice === 'skip' ? [] : [{ ...picked, onConflict: choice }],
            )
            if (wanted.length === 0) return
            enqueue(wanted, rootId, currentPath)
          }}
        />
      )}
    </div>
  )
}
